import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewApi, cycleApi, employeeApi } from '../../services/api';
import { PageHeader, Spinner, EmptyState, Modal } from '../../components/shared/index';
import { PlusIcon, ClockIcon, ArrowRightIcon, InboxIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const REVIEW_TYPES = [
  { value: 'manager',      label: 'Manager review' },
  { value: 'self',         label: 'Self review' },
  { value: 'peer',         label: 'Peer review' },
  { value: 'upward',       label: 'Upward review' },
  { value: 'skip_level',   label: 'Skip-level review' },
];

const STATUS_STYLES = {
  pending:      'bg-gray-100   text-gray-700',
  in_progress:  'bg-blue-50    text-blue-700',
  submitted:    'bg-green-50   text-green-700',
};

function TypeBadge({ type }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100 capitalize">
      {String(type || '').replace('_', ' ')}
    </span>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize', STATUS_STYLES[status] || STATUS_STYLES.pending)}>
      {String(status || 'pending').replace('_', ' ')}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
}

function CreateReviewForm({ onClose, onCreated }) {
  const [form, setForm] = useState({
    cycle_id: '', employee_id: '', reviewer_id: '', template_id: '', type: 'manager', due_at: '',
  });

  const { data: cycles }    = useQuery({ queryKey: ['cycles', { status: 'active' }], queryFn: () => cycleApi.list({ status: 'active' }).then(r => r.data?.data || r.data || []) });
  const { data: employees } = useQuery({ queryKey: ['employees'], queryFn: () => employeeApi.list().then(r => r.data?.data || r.data || []) });
  const { data: templates } = useQuery({ queryKey: ['review-templates'], queryFn: () => reviewApi.templates.list().then(r => r.data?.data || r.data || []) });

  const createMut = useMutation({
    mutationFn: (data) => reviewApi.create(data),
    onSuccess:  () => { toast.success('Review created'); onCreated(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const valid = form.cycle_id && form.employee_id && form.reviewer_id && form.type;

  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    const payload = { ...form };
    if (!payload.template_id) delete payload.template_id;
    if (!payload.due_at)      delete payload.due_at;
    createMut.mutate(payload);
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Cycle</label>
          <select className="input" value={form.cycle_id} onChange={set('cycle_id')} required>
            <option value="">Select cycle…</option>
            {cycles?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Review type</label>
          <select className="input" value={form.type} onChange={set('type')} required>
            {REVIEW_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Reviewee</label>
          <select className="input" value={form.employee_id} onChange={set('employee_id')} required>
            <option value="">Select employee…</option>
            {employees?.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Reviewer</label>
          <select className="input" value={form.reviewer_id} onChange={set('reviewer_id')} required>
            <option value="">Select reviewer…</option>
            {employees?.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Template <span className="text-gray-400 font-normal">(optional)</span></label>
          <select className="input" value={form.template_id} onChange={set('template_id')}>
            <option value="">Default for type</option>
            {templates?.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Due date</label>
          <input type="date" className="input" value={form.due_at} onChange={set('due_at')} />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={!valid || createMut.isPending} className="btn-primary disabled:opacity-40">
          {createMut.isPending ? 'Creating…' : 'Create review'}
        </button>
      </div>
    </form>
  );
}

export default function ReviewList() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('pending'); // pending | all
  const [filters, setFilters] = useState({ cycle_id: '', status: '', type: '', page: 1 });
  const [creating, setCreating] = useState(false);

  const { data: pending, isLoading: pendingLoading } = useQuery({
    queryKey: ['my-pending-reviews'],
    queryFn:  () => reviewApi.myPending().then(r => r.data?.data || r.data || []),
    enabled:  tab === 'pending',
  });

  const { data: allData, isLoading: allLoading } = useQuery({
    queryKey: ['reviews', filters],
    queryFn:  () => reviewApi.list({
      cycle_id: filters.cycle_id || undefined,
      status:   filters.status   || undefined,
      type:     filters.type     || undefined,
      page:     filters.page,
    }).then(r => r.data),
    enabled:  tab === 'all',
    keepPreviousData: true,
  });

  const { data: cycles } = useQuery({
    queryKey: ['cycles-all'],
    queryFn:  () => cycleApi.list().then(r => r.data?.data || r.data || []),
  });

  const isLoading = tab === 'pending' ? pendingLoading : allLoading;
  const allReviews   = allData?.data || [];
  const pagination   = allData?.meta || allData?.pagination || null;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['my-pending-reviews'] });
    qc.invalidateQueries({ queryKey: ['reviews'] });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Performance reviews"
        subtitle="Write your reviews, track submissions, and manage the cycle workload."
        actions={
          <button onClick={() => setCreating(true)} className="btn-primary">
            <PlusIcon className="w-4 h-4" />
            Create review
          </button>
        }
      />

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-gray-200">
        {[
          ['pending', 'My pending', pending?.length],
          ['all',     'All reviews (HR)'],
        ].map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={clsx(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            {label}{typeof count === 'number' ? <span className="ml-1.5 text-xs text-gray-400">({count})</span> : null}
          </button>
        ))}
      </div>

      {isLoading && <div className="flex justify-center p-20"><Spinner size="lg" /></div>}

      {/* Pending tab */}
      {!isLoading && tab === 'pending' && (
        pending?.length ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {['Reviewee', 'Cycle', 'Type', 'Due', 'Status', ''].map(h => <th key={h} className="th">{h}</th>)}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {pending.map(r => {
                  const overdue = r.due_at && new Date(r.due_at) < new Date() && r.status !== 'submitted';
                  return (
                    <tr key={r.id} className="tr-hover">
                      <td className="td">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">
                            {r.employee?.name?.[0] || '?'}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{r.employee?.name || '—'}</p>
                            <p className="text-xs text-gray-400 capitalize">{r.employee?.role?.replace('_', ' ') || ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="td text-sm text-gray-700">{r.cycle?.name || '—'}</td>
                      <td className="td"><TypeBadge type={r.type} /></td>
                      <td className="td">
                        <div className={clsx('flex items-center gap-1 text-sm', overdue ? 'text-red-600 font-medium' : 'text-gray-700')}>
                          <ClockIcon className="w-4 h-4" />
                          {formatDate(r.due_at)}
                          {overdue && <span className="text-xs text-red-500">(overdue)</span>}
                        </div>
                      </td>
                      <td className="td"><StatusBadge status={r.status} /></td>
                      <td className="td">
                        <Link to={`/app/reviews/${r.id}`} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
                          Open <ArrowRightIcon className="w-3.5 h-3.5" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="You're all caught up"
            description="No reviews are waiting on you right now. Once your manager or HR assigns a review, it will appear here."
          />
        )
      )}

      {/* All tab */}
      {!isLoading && tab === 'all' && (
        <>
          <div className="card p-4 mb-4 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[180px]">
              <label className="label">Cycle</label>
              <select className="input" value={filters.cycle_id} onChange={e => setFilters(f => ({ ...f, cycle_id: e.target.value, page: 1 }))}>
                <option value="">All cycles</option>
                {cycles?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="label">Status</label>
              <select className="input" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
                <option value="">Any status</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In progress</option>
                <option value="submitted">Submitted</option>
              </select>
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="label">Type</label>
              <select className="input" value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value, page: 1 }))}>
                <option value="">Any type</option>
                {REVIEW_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            {(filters.cycle_id || filters.status || filters.type) && (
              <button
                onClick={() => setFilters({ cycle_id: '', status: '', type: '', page: 1 })}
                className="btn-secondary btn-sm"
              >
                Clear
              </button>
            )}
          </div>

          {allReviews.length === 0 ? (
            <EmptyState
              title="No reviews found"
              description="Try adjusting your filters, or create a new review for the active cycle."
              action={<button onClick={() => setCreating(true)} className="btn-primary"><PlusIcon className="w-4 h-4" />Create review</button>}
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    {['Reviewee', 'Reviewer', 'Cycle', 'Type', 'Due', 'Status', 'Submitted', ''].map(h => <th key={h} className="th">{h}</th>)}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {allReviews.map(r => (
                    <tr key={r.id} className="tr-hover">
                      <td className="td text-sm font-medium text-gray-900">{r.employee?.name || '—'}</td>
                      <td className="td text-sm text-gray-700">{r.reviewer?.name || '—'}</td>
                      <td className="td text-sm text-gray-700">{r.cycle?.name || '—'}</td>
                      <td className="td"><TypeBadge type={r.type} /></td>
                      <td className="td text-sm text-gray-700">{formatDate(r.due_at)}</td>
                      <td className="td"><StatusBadge status={r.status} /></td>
                      <td className="td text-sm text-gray-500">{r.submitted_at ? formatDate(r.submitted_at) : '—'}</td>
                      <td className="td">
                        <Link to={`/app/reviews/${r.id}`} className="text-sm text-blue-600 hover:underline">View</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination && pagination.last_page > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
              <span>Page {pagination.current_page} of {pagination.last_page} · {pagination.total} total</span>
              <div className="flex gap-2">
                <button
                  disabled={filters.page <= 1}
                  onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
                  className="btn-secondary btn-sm disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  disabled={filters.page >= pagination.last_page}
                  onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
                  className="btn-secondary btn-sm disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="Create a new review" size="lg">
        {creating && (
          <CreateReviewForm
            onClose={() => setCreating(false)}
            onCreated={() => { invalidate(); setCreating(false); }}
          />
        )}
      </Modal>
    </div>
  );
}
